import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  completePendingDraftRemoval,
  hasPendingDraftCleanupForScope,
  recordPendingDraftRemoval,
  retryPendingDraftCleanupForScope,
} from "./draftCleanupObligations";
import { buildComposerDraftKey } from "./draftKey";
import { registerComposerDraftFlush } from "./draftFlushRegistry";
import { draftRepository, DraftStorageError } from "./draftRepository";
import {
  readDraftSessionShadow,
  removeDraftSessionShadow,
  removeDraftSessionShadowForScope,
  writeDraftSessionShadow,
} from "./draftSessionShadow";
import type { ComposerDraft, ComposerDraftScope } from "./types";

const SAVE_DEBOUNCE_MS = 400;

export interface UseComposerDraftOptions {
  scope: ComposerDraftScope;
  content: string;
  hadAttachments: boolean;
  enabled: boolean;
  onRestore: (content: string) => void;
}

export interface UseComposerDraftResult {
  isRestoring: boolean;
  restoredDraft: ComposerDraft | null;
  isTargetUnavailable: boolean;
  discardDraft: () => Promise<void>;
  flushDraft: () => Promise<void>;
  storageError: DraftStorageError | null;
}

interface DraftSnapshot {
  content: string;
  hadAttachments: boolean;
}

interface ActiveDraft {
  key: string;
  scope: ComposerDraftScope;
  content: string;
  hadAttachments: boolean;
  enabled: boolean;
  isRestoring: boolean;
  persisted: DraftSnapshot;
  failedSnapshot: DraftSnapshot | null;
  targetState: ComposerDraft["targetState"];
  createdAt: number | null;
  timer: ReturnType<typeof setTimeout> | null;
  writeQueue: Promise<void>;
}

interface DraftViewState {
  key: string;
  isRestoring: boolean;
  restoredDraft: ComposerDraft | null;
  storageError: DraftStorageError | null;
  isTargetUnavailable: boolean;
}

const snapshot = (draft: ActiveDraft): DraftSnapshot => ({
  content: draft.content,
  hadAttachments: draft.hadAttachments,
});

const snapshotsMatch = (left: DraftSnapshot, right: DraftSnapshot): boolean =>
  left.content === right.content &&
  left.hadAttachments === right.hadAttachments;

const isDirty = (draft: ActiveDraft): boolean =>
  !snapshotsMatch(snapshot(draft), draft.persisted);

const clearSaveTimer = (draft: ActiveDraft): void => {
  if (draft.timer === null) return;
  clearTimeout(draft.timer);
  draft.timer = null;
};

const enqueueDraftWrite = <T>(
  draft: ActiveDraft,
  operation: () => Promise<T>,
): Promise<T> => {
  const write = draft.writeQueue.then(operation);
  draft.writeQueue = write.then(
    () => undefined,
    () => undefined,
  );
  return write;
};

const createActiveDraft = (
  scope: ComposerDraftScope,
  content: string,
  hadAttachments: boolean,
  enabled: boolean,
): ActiveDraft => ({
  key: buildComposerDraftKey(scope),
  scope,
  content,
  hadAttachments,
  enabled,
  isRestoring: enabled,
  persisted: { content: "", hadAttachments: false },
  failedSnapshot: null,
  targetState: undefined,
  createdAt: null,
  timer: null,
  writeQueue: Promise.resolve(),
});

export const useComposerDraft = ({
  scope,
  content,
  hadAttachments,
  enabled,
  onRestore,
}: UseComposerDraftOptions): UseComposerDraftResult => {
  const scopeKey = buildComposerDraftKey(scope);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  const latestInputRef = useRef({
    key: scopeKey,
    scope,
    content,
    hadAttachments,
    enabled,
  });
  latestInputRef.current = {
    key: scopeKey,
    scope,
    content,
    hadAttachments,
    enabled,
  };

  const activeRef = useRef<ActiveDraft | null>(null);
  if (activeRef.current === null) {
    activeRef.current = createActiveDraft(
      scope,
      content,
      hadAttachments,
      enabled,
    );
  } else if (activeRef.current.key === scopeKey) {
    activeRef.current.scope = scope;
    activeRef.current.content = content;
    activeRef.current.hadAttachments = hadAttachments;
    activeRef.current.enabled = enabled;
  }

  const mountedRef = useRef(false);
  const transitionRef = useRef(0);
  const [, setRevision] = useState(0);
  const [viewState, setViewState] = useState<DraftViewState>(() => ({
    key: scopeKey,
    isRestoring: enabled,
    restoredDraft: null,
    storageError: null,
    isTargetUnavailable: false,
  }));

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const updateViewState = useCallback(
    (update: (current: DraftViewState) => DraftViewState): void => {
      if (mountedRef.current) setViewState(update);
    },
    [],
  );

  const persistDraft = useCallback(
    (active: ActiveDraft): Promise<void> => {
      clearSaveTimer(active);
      return enqueueDraftWrite(active, async () => {
        if (
          !active.enabled ||
          !isDirty(active) ||
          hasPendingDraftCleanupForScope(active.scope)
        ) {
          return;
        }

        const saving = snapshot(active);
        const now = Date.now();
        const createdAt = active.createdAt ?? now;
        const result = await draftRepository.put({
          version: 1,
          key: active.key,
          scope: active.scope,
          content: saving.content,
          hadAttachments: saving.hadAttachments,
          createdAt,
          updatedAt: now,
          targetState: active.targetState,
        });
        await draftRepository.flush();

        if (result instanceof DraftStorageError) {
          active.failedSnapshot = saving;
          updateViewState((current) => ({
            ...current,
            storageError: result,
          }));
          return;
        }

        removeDraftSessionShadow({
          version: 1,
          key: active.key,
          scope: active.scope,
          content: saving.content,
          hadAttachments: saving.hadAttachments,
          createdAt,
          updatedAt: now,
          targetState: active.targetState,
        });

        active.persisted = saving;
        active.failedSnapshot = null;
        active.createdAt = createdAt;
        updateViewState((current) => ({
          ...current,
          storageError: null,
        }));
        if (mountedRef.current) setRevision((revision) => revision + 1);
      });
    },
    [updateViewState],
  );

  useEffect(() => {
    const transition = transitionRef.current + 1;
    transitionRef.current = transition;
    const previous = activeRef.current;
    const input = latestInputRef.current;
    if (input.key !== scopeKey || input.enabled !== enabled) return;
    const next =
      previous?.key === input.key
        ? previous
        : createActiveDraft(
            input.scope,
            input.content,
            input.hadAttachments,
            input.enabled,
          );
    activeRef.current = next;
    next.enabled = input.enabled;
    next.isRestoring = input.enabled;
    clearSaveTimer(next);

    setViewState({
      key: input.key,
      isRestoring: input.enabled,
      restoredDraft: null,
      storageError: null,
      isTargetUnavailable: false,
    });

    void (async () => {
      if (previous && previous !== next) {
        await persistDraft(previous);
      }
      if (transitionRef.current !== transition) return;

      if (!input.enabled) {
        next.isRestoring = false;
        updateViewState((current) => ({
          ...current,
          isRestoring: false,
        }));
        return;
      }

      if (hasPendingDraftCleanupForScope(next.scope)) {
        const cleanupFailure = await retryPendingDraftCleanupForScope(
          next.scope,
        );
        if (
          transitionRef.current !== transition ||
          activeRef.current !== next
        ) {
          return;
        }
        if (hasPendingDraftCleanupForScope(next.scope)) {
          next.isRestoring = false;
          updateViewState((current) => ({
            ...current,
            isRestoring: false,
            storageError: cleanupFailure,
          }));
          return;
        }
      }

      const restoreStartedWith = snapshot(next);
      const result = await draftRepository.get(next.scope);
      if (transitionRef.current !== transition || activeRef.current !== next) {
        return;
      }

      next.isRestoring = false;
      if (result instanceof DraftStorageError) {
        updateViewState((current) => ({
          ...current,
          isRestoring: false,
          storageError: result,
        }));
        return;
      }

      const shadow = readDraftSessionShadow(next.scope);
      const restored =
        shadow && (!result || shadow.updatedAt >= result.updatedAt)
          ? {
              ...shadow,
              ...(result?.targetState === "local-only"
                ? { targetState: "local-only" as const }
                : {}),
            }
          : result;

      if (result) {
        next.createdAt = result.createdAt;
        next.persisted = {
          content: result.content,
          hadAttachments: result.hadAttachments,
        };
        next.targetState = result.targetState;
      }

      const editorIsStillEmpty =
        restoreStartedWith.content.length === 0 &&
        !restoreStartedWith.hadAttachments &&
        snapshotsMatch(snapshot(next), restoreStartedWith);
      const restoredDraft = restored && editorIsStillEmpty ? restored : null;
      if (restoredDraft) {
        next.createdAt = restoredDraft.createdAt;
        next.targetState = restoredDraft.targetState;
        next.content = restoredDraft.content;
        next.hadAttachments = restoredDraft.hadAttachments;
        onRestoreRef.current(restoredDraft.content);
      }

      updateViewState((current) => ({
        ...current,
        isRestoring: false,
        restoredDraft,
        storageError: null,
        isTargetUnavailable: restored?.targetState === "local-only",
      }));
      if (mountedRef.current) setRevision((revision) => revision + 1);
    })();
  }, [enabled, persistDraft, scopeKey, updateViewState]);

  const currentView =
    viewState.key === scopeKey
      ? viewState
      : {
          key: scopeKey,
          isRestoring: enabled,
          restoredDraft: null,
          storageError: null,
          isTargetUnavailable: false,
        };
  const active = activeRef.current;
  const currentSnapshot = active ? snapshot(active) : null;
  const dirty = active?.key === scopeKey && enabled && isDirty(active);
  const automaticSaveAllowed =
    dirty &&
    !currentView.isRestoring &&
    currentSnapshot !== null &&
    (!active.failedSnapshot ||
      !snapshotsMatch(currentSnapshot, active.failedSnapshot));
  const automaticSaveFingerprint = currentSnapshot
    ? JSON.stringify([currentSnapshot.content, currentSnapshot.hadAttachments])
    : "";

  useLayoutEffect(() => {
    const current = activeRef.current;
    if (!current || current.key !== scopeKey || !enabled) return;
    const emergencyHadAttachments =
      hadAttachments || Boolean(currentView.restoredDraft?.hadAttachments);
    const currentInput = {
      content,
      hadAttachments: emergencyHadAttachments,
    };
    if (snapshotsMatch(currentInput, current.persisted)) return;
    const now = Date.now();
    writeDraftSessionShadow({
      version: 1,
      key: current.key,
      scope: current.scope,
      content,
      hadAttachments: emergencyHadAttachments,
      createdAt: current.createdAt ?? now,
      updatedAt: now,
      targetState: current.targetState,
    });
  }, [content, currentView.restoredDraft, enabled, hadAttachments, scopeKey]);

  useEffect(() => {
    const current = activeRef.current;
    if (!current || current.key !== scopeKey) return;
    clearSaveTimer(current);
    if (!automaticSaveAllowed || automaticSaveFingerprint.length === 0) return;

    current.timer = setTimeout(() => {
      current.timer = null;
      void persistDraft(current);
    }, SAVE_DEBOUNCE_MS);

    return () => clearSaveTimer(current);
  }, [automaticSaveAllowed, automaticSaveFingerprint, persistDraft, scopeKey]);

  // A scope can unmount before its debounce fires (for example while scrolling
  // between channel layouts). Hand the latest snapshot to the repository queue.
  useEffect(
    () => () => {
      const current = activeRef.current;
      if (!current) return;
      clearSaveTimer(current);
      void persistDraft(current);
    },
    [persistDraft],
  );

  const flushDraft = useCallback(async (): Promise<void> => {
    const current = activeRef.current;
    if (!current) return;
    await persistDraft(current);
  }, [persistDraft]);

  useEffect(() => {
    if (!dirty) return;
    return registerComposerDraftFlush(flushDraft);
  }, [dirty, flushDraft]);

  const discardDraft = useCallback(async (): Promise<void> => {
    const current = activeRef.current;
    if (!current) return;
    clearSaveTimer(current);

    recordPendingDraftRemoval(current.scope);
    try {
      const result = await enqueueDraftWrite(current, async () => {
        const removal = await draftRepository.remove(current.scope);
        await draftRepository.flush();
        return removal;
      });
      if (result instanceof DraftStorageError) throw result;
    } catch (error) {
      const storageError =
        error instanceof DraftStorageError
          ? error
          : new DraftStorageError("unavailable", error);
      updateViewState((state) => ({ ...state, storageError }));
      throw storageError;
    }

    completePendingDraftRemoval(current.scope);
    removeDraftSessionShadowForScope(current.scope);

    current.persisted = snapshot(current);
    current.failedSnapshot = null;
    current.createdAt = null;
    updateViewState((state) => ({
      ...state,
      restoredDraft: null,
      storageError: null,
    }));
    if (mountedRef.current) setRevision((revision) => revision + 1);
  }, [updateViewState]);

  return {
    isRestoring: currentView.isRestoring,
    restoredDraft: currentView.restoredDraft,
    isTargetUnavailable: currentView.isTargetUnavailable,
    discardDraft,
    flushDraft,
    storageError: currentView.storageError,
  };
};
